// src/pages/Invoices/InvoiceDetailModal.jsx

import React, { useState, useEffect } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../context/AuthContext';

const styles = {
    overlay: {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.6)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
    },
    modal: {
        background: 'white',
        padding: '40px',
        borderRadius: '8px',
        width: '90%',
        maxWidth: '800px',
        maxHeight: '90vh',
        overflowY: 'auto',
        fontFamily: '"Inter", sans-serif',
        position: 'relative'
    },
    header: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        borderBottom: '2px solid #06C167',
        paddingBottom: '20px',
        marginBottom: '20px',
    },
    closeButton: {
        position: 'absolute',
        top: '15px',
        right: '15px',
        border: 'none',
        background: 'transparent',
        fontSize: '28px',
        cursor: 'pointer',
        color: '#6c757d'
    },
    invoiceInfo: {
        display: 'flex',
        justifyContent: 'space-between',
        marginBottom: '30px',
        fontSize: '14px',
    },
    companyInfo: {
        textAlign: 'left'
    },
    customerInfo: {
        textAlign: 'right'
    },
    table: {
        width: '100%',
        borderCollapse: 'collapse',
        marginTop: '20px',
    },
    th: {
        background: '#f8f9fa',
        padding: '12px',
        textAlign: 'left',
        borderBottom: '2px solid #dee2e6',
    },
    td: {
        padding: '12px',
        borderBottom: '1px solid #dee2e6',
    },
    totals: {
        marginTop: '20px',
        marginLeft: 'auto',
        width: '280px',
    },
    totalRow: {
        display: 'flex',
        justifyContent: 'space-between',
        padding: '8px 0',
    },
    grandTotal: {
        fontWeight: 'bold',
        borderTop: '2px solid #333',
        paddingTop: '10px',
        fontSize: '18px'
    },
    footer: {
        marginTop: '30px',
        paddingTop: '20px',
        borderTop: '1px solid #dee2e6',
        textAlign: 'right'
    },
    printButton: {
        padding: '10px 20px',
        border: 'none',
        background: '#06C167',
        color: 'white',
        borderRadius: '5px',
        cursor: 'pointer',
        fontSize: '16px',
    }
};

function InvoiceDetailModal({ invoice, onClose, canViewPrices }) {
    const { currentUser } = useAuth();
    const [companyInfo, setCompanyInfo] = useState(null);

    useEffect(() => {
        if (!currentUser) return;
        const fetchCompanyInfo = async () => {
            const companyDocRef = doc(db, 'companies', currentUser.uid);
            const docSnap = await getDoc(companyDocRef);
            if (docSnap.exists()) {
                setCompanyInfo(docSnap.data());
            }
        };
        fetchCompanyInfo();
    }, [currentUser]);

    if (!invoice) return null;

    const handlePrint = () => {
        window.print();
    };

    return (
        <div style={styles.overlay} onClick={onClose}>
            <div style={styles.modal} onClick={e => e.stopPropagation()}>
                <style>
                    {`
                        @media print {
                            body * {
                                visibility: hidden;
                            }
                            .invoice-modal, .invoice-modal * {
                                visibility: visible;
                            }
                            .invoice-modal {
                                position: absolute;
                                left: 0;
                                top: 0;
                                width: 100%;
                                max-width: 100%;
                                padding: 0;
                                box-shadow: none;
                                border-radius: 0;
                            }
                            .no-print {
                                display: none;
                            }
                        }
                    `}
                </style>
                
                <div style={styles.header} className="no-print">
                    <div>
                        <h2 style={{ margin: 0, fontSize: '24px', fontWeight: '800', color: '#1e293b' }}>Fatura Detayı</h2>
                        <p style={{ margin: '4px 0 0 0', color: '#64748b', fontSize: '14px' }}>#{invoice.invoiceNumber || invoice.id.substring(0, 8).toUpperCase()}</p>
                    </div>
                    <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                        <button 
                            onClick={handlePrint} 
                            style={{
                                ...styles.printButton,
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                background: '#10b981'
                            }}
                        >
                            <span>🖨️</span> Yazdır
                        </button>
                        <button 
                            onClick={onClose} 
                            style={{
                                background: '#f1f5f9',
                                border: 'none',
                                width: '40px',
                                height: '40px',
                                borderRadius: '50%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: 'pointer',
                                fontSize: '20px',
                                color: '#64748b'
                            }}
                        >
                            &times;
                        </button>
                    </div>
                </div>

                <div className="invoice-modal">
                    <div style={{ 
                        display: 'grid', 
                        gridTemplateColumns: '1fr 1fr', 
                        gap: '24px', 
                        marginBottom: '32px',
                        background: '#f8fafc',
                        padding: '24px',
                        borderRadius: '20px',
                        border: '1px solid #e2e8f0'
                    }}>
                        <div>
                            <div style={{ fontSize: '12px', fontWeight: '600', color: '#94a3b8', textTransform: 'uppercase', marginBottom: '8px' }}>Satıcı Firma</div>
                            <div style={{ fontSize: '16px', fontWeight: '700', color: '#1e293b' }}>{companyInfo?.companyName || 'Firma Adı'}</div>
                            <div style={{ fontSize: '14px', color: '#64748b', marginTop: '4px' }}>{companyInfo?.address || 'Firma Adresi'}</div>
                            <div style={{ fontSize: '13px', color: '#64748b', marginTop: '4px' }}>VD: {companyInfo?.taxOffice || '-'} / {companyInfo?.taxNumber || '-'}</div>
                        </div>
                        <div style={{ textAlign: 'right' }}>
                            <div style={{ fontSize: '12px', fontWeight: '600', color: '#94a3b8', textTransform: 'uppercase', marginBottom: '8px' }}>Müşteri</div>
                            <div style={{ fontSize: '16px', fontWeight: '700', color: '#1e293b' }}>{invoice.customerName}</div>
                            <div style={{ fontSize: '14px', color: '#64748b', marginTop: '4px' }}><strong>Tarih:</strong> {new Date(invoice.invoiceDate.seconds * 1000).toLocaleDateString('tr-TR')}</div>
                            {invoice.dueDate?.seconds && (
                                <div style={{ fontSize: '14px', color: (invoice.status !== 'Tamamlandı' && new Date(invoice.dueDate.seconds * 1000) < new Date()) ? '#dc2626' : '#64748b', marginTop: '4px' }}>
                                    <strong>Vade:</strong> {new Date(invoice.dueDate.seconds * 1000).toLocaleDateString('tr-TR')}
                                </div>
                            )}
                        </div>
                    </div>

                    <div style={{ border: '1px solid #e2e8f0', borderRadius: '16px', overflow: 'hidden', background: 'white' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead style={{ background: '#f8fafc' }}>
                                <tr>
                                    <th style={{ padding: '16px', textAlign: 'left', borderBottom: '1px solid #e2e8f0', color: '#475569', fontSize: '13px' }}>Ürün Adı</th>
                                    <th style={{ padding: '16px', textAlign: 'center', borderBottom: '1px solid #e2e8f0', color: '#475569', fontSize: '13px' }}>Miktar</th>
                                    <th style={{ padding: '16px', textAlign: 'right', borderBottom: '1px solid #e2e8f0', color: '#475569', fontSize: '13px' }}>Birim Fiyat</th>
                                    <th style={{ padding: '16px', textAlign: 'right', borderBottom: '1px solid #e2e8f0', color: '#475569', fontSize: '13px' }}>Toplam</th>
                                </tr>
                            </thead>
                            <tbody>
                                {invoice.lineItems.map((item, index) => (
                                    <tr key={index}>
                                        <td style={{ padding: '16px', borderBottom: '1px solid #f1f5f9', color: '#1e293b', fontWeight: '500' }}>{item.name}</td>
                                        <td style={{ padding: '16px', borderBottom: '1px solid #f1f5f9', textAlign: 'center', color: '#1e293b' }}>{item.quantity}</td>
                                        <td style={{ padding: '16px', borderBottom: '1px solid #f1f5f9', textAlign: 'right', color: '#1e293b' }}>{canViewPrices ? `${item.price.toFixed(2)} ₺` : '***'}</td>
                                        <td style={{ padding: '16px', borderBottom: '1px solid #f1f5f9', textAlign: 'right', color: '#1e293b', fontWeight: '700' }}>{canViewPrices ? `${item.total.toFixed(2)} ₺` : '***'}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    <div style={{ marginTop: '32px', display: 'flex', justifyContent: 'flex-end' }}>
                        <div style={{ width: '320px', background: '#f8fafc', padding: '24px', borderRadius: '20px', border: '1px solid #e2e8f0' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px', fontSize: '14px', color: '#64748b' }}>
                                <span>Ara Toplam</span>
                                <span>{canViewPrices ? `${(invoice.subTotal || 0).toFixed(2)} ₺` : '***'}</span>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px', fontSize: '14px', color: '#64748b' }}>
                                <span>KDV Tutarı</span>
                                <span>{canViewPrices ? `${(invoice.vatAmount || 0).toFixed(2)} ₺` : '***'}</span>
                            </div>
                            <div style={{ height: '1px', background: '#e2e8f0', margin: '16px 0' }} />
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '20px', fontWeight: '800', color: '#1e293b' }}>
                                <span>Genel Toplam</span>
                                <span style={{ color: '#06C167' }}>{canViewPrices ? `${invoice.total.toFixed(2)} ₺` : '***'}</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default InvoiceDetailModal;
